import { get } from 'lodash';
import { config } from 'api';
import { ensureProducts } from './products';
import errorHandler from 'utils/errorHandler';
import { Url } from 'lib/utils/url';
import { BetaFeatures as FoodProfileBetaFeatures } from 'apps/foodProfile/consts';
import { fetchProductsSuitability } from 'apps/foodProfile/actions/foodProfile';

export const ADD_PRODUCT_DETAILS = 'ADD_PRODUCT_DETAILS';
export const ADD_STATIC_CONTENT = 'ADD_STATIC_CONTENT';
export const ADD_SIMILAR_PRODUCTS = 'ADD_SIMILAR_PRODUCTS';
export const ADD_RECOMMENDATIONS = 'ADD_RECOMMENDATIONS';
export const ADD_RECIPES = 'ADD_RECIPES';
export const ADD_OPTION = 'ADD_OPTION';
export const UPDATE_PRODUCT_BASIC_INFO = 'UPDATE_PRODUCT_BASIC_INFO';
export const CLEAR_OPTIONS = 'CLEAR_OPTIONS';
export const ADD_CRITEO_SPONSORED_PRODUCTS = 'ADD_CRITEO_SPONSORED_PRODUCTS';

/**
 * Adds the given product details to the repository.
 *
 * @param productDetails
 * @returns {{type: string, productDetails: *}}
 */
export function addProductDetails(productDetails) {
  return {
    type: ADD_PRODUCT_DETAILS,
    productDetails,
  };
}

function getProductSectionsBarcodes(productSections) {
  return productSections.reduce(
    (sections, section) => ([
      ...sections,
      ...section.fops.reduce(
        (fops, fop) => ([
          ...fops,
          fop.product.gtin,
        ]),
        []
      ),
    ]),
    []
  );
}

export function addSimilarProducts(sku, similarProducts) {
  return (dispatch, getState) => {
    const isFoodMaestroEnabled = get(getState(), 'user.betaFeatures', [])
      .includes(FoodProfileBetaFeatures.FOODMAESTRO_ENABLED);
    const productSections = similarProducts.sections || [];
    const barcodes = getProductSectionsBarcodes(productSections);

    dispatch({
      type: ADD_SIMILAR_PRODUCTS,
      sku,
      similarProducts,
    });
    if (isFoodMaestroEnabled) dispatch(fetchProductsSuitability(barcodes));
  };
}

export function addRecommendations(sku, recommendations) {
  return (dispatch, getState) => {
    const isFoodMaestroEnabled = get(getState(), 'user.betaFeatures', [])
      .includes(FoodProfileBetaFeatures.FOODMAESTRO_ENABLED);
    const productSections = recommendations.sections || [];
    const barcodes = getProductSectionsBarcodes(productSections);

    dispatch({
      type: ADD_RECOMMENDATIONS,
      sku,
      recommendations,
    });
    if (isFoodMaestroEnabled) dispatch(fetchProductsSuitability(barcodes));
  };
}

export function addRecipes(sku, { recipes }) {
  return {
    type: ADD_RECIPES,
    sku,
    recipes,
  };
}

export function addOptions(sku, index, value) {
  return {
    type: ADD_OPTION,
    sku,
    index,
    value,
  };
}

export function clearOptions(sku, index) {
  return {
    type: CLEAR_OPTIONS,
    sku,
    index,
  };
}

export function addCriteoSponsoredProducts(sku, criteoSponsoredProducts) {
  return {
    type: ADD_CRITEO_SPONSORED_PRODUCTS,
    sku,
    criteoSponsoredProducts,
  };
}

export function updateProductBasicInfo(parentSku, data) {
  return {
    type: UPDATE_PRODUCT_BASIC_INFO,
    parentSku,
    data,
  };
}

export function fetchProductBasicInfo(sku, parentSku) {
  return (dispatch, getState, { network }) =>
    network.get(`${config.apiBasePath}/products/${sku}/details`)
      .then(data => {
        dispatch(updateProductBasicInfo(parentSku, data));
      })
      .catch(errorHandler);
}

/**
 * Checks whether product details for requested sku exist in cache and if not, fetch it from the server.
 *
 * @param sku
 * @param flashSales
 * @returns {function(*, *)}
 */

export function ensureProductDetails(sku, flashSales) {
  return (dispatch, getState, { network }) => {
    const productDetailsInCache = getState().products.productDetailsBySku;
    const productType = get(productDetailsInCache[sku], 'product.type', '');
    if (productDetailsInCache[sku] && productType === 'REGULAR') {
      return Promise.resolve(); // product details already present in store
    }

    const url = new Url(`${config.apiBasePath}/products/${sku}/details`);
    if (flashSales) {
      url.addQueryParameter('type', 'FLASH_SALE');
    }

    return network.get(url.toString())
      .then(productDetails => dispatch(addProductDetails(productDetails)))
      .catch(err => {
        // TODO add proper error handling
        console.log('[ensureProductDetails]', err); // eslint-disable-line no-console
        return dispatch(ensureProducts([{ sku }]));
      }); // try to fetch the simple product data to render at least basic info
  };
}

export function addStaticContent(staticContent) {
  return {
    type: ADD_STATIC_CONTENT,
    staticContent,
  };
}

export function fetchStaticContent() {
  return (dispatch, getState, { network }) =>
    network.get(`${config.apiBasePath}/products/static-content`)
      .then(staticContent => dispatch(addStaticContent(staticContent)))
      .catch(errorHandler);
}

export function fetchSimilarProducts(sku) {
  return (dispatch, getState, { network }) =>
    network.get(`${config.apiBasePath}/products/${sku}/alternatives`)
      .then(similarProducts => dispatch(addSimilarProducts(sku, similarProducts)))
      .catch(errorHandler);
}

export function fetchRecommendations(sku) {
  return (dispatch, getState, { network }) =>
    network.get(`${config.apiBasePath}/products/${sku}/recommendations`)
      .then(recommendations => dispatch(addRecommendations(sku, recommendations)))
      .catch(errorHandler);
}

export function fetchCriteoSponsoredProducts(sku) {
  return (dispatch, getState, { network }) =>
    network.get(`${config.apiBasePath}/products/${sku}/sponsored`)
      .then(data => {
        dispatch(addCriteoSponsoredProducts(sku, data));
      })
      .catch(errorHandler);
}

export function fetchRecipes(sku) {
  return (dispatch, getState, { network }) =>
    network.get(`${config.apiBasePath}/products/${sku}/recipes`)
      .then(recipes => dispatch(addRecipes(sku, recipes)))
      .catch(errorHandler);
}
